// src/components/Layout/DashboardLayout.tsx

import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Collapse,
  useTheme,
  ButtonBase,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  CalendarMonth as CalendarMonthIcon,
  VolunteerActivism as VolunteerActivismIcon,
  ReceiptLong as ReceiptLongIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { toggleSidebar, setThemeMode } from '../../store/slices/uiSlice';
import { logout } from '../../store/slices/authSlice';
import { useAppDispatch }from '../../hooks/useAppDispatch';
import Logo from '../Logo';

interface MenuItemType {
  text: string;
  icon: React.ReactNode;
  path: string;
  roles: string[];
  children?: MenuItemType[];
}

const menuItems: MenuItemType[] = [
  {
    text: 'Panel',
    icon: <DashboardIcon />,
    path: '/dashboard',
    roles: ['admin', 'manager', 'viewer']
  },
  {
    text: 'Académico',
    icon: <SchoolIcon />,
    path: '#',
    roles: ['admin', 'manager', 'viewer'],
    children: [
      {
        text: 'Estudiantes',
        icon: <SchoolIcon />,
        path: '/students',
        roles: ['admin', 'manager', 'viewer']
      },
      {
        text: 'Padres',
        icon: <PeopleIcon />,
        path: '/parents',
        roles: ['admin', 'manager']
      },
      {
        text: 'Profesores',
        icon: <PeopleIcon />,
        path: '/teachers',
        roles: ['admin', 'manager']
      },
      {
        text: 'Cursos Escolares',
        icon: <CalendarMonthIcon />,
        path: '/school-years',
        roles: ['admin', 'manager', 'viewer']
      }
    ]
  },
  {
    text: 'Pagos',
    icon: <PaymentIcon />,
    path: '#',
    roles: ['admin', 'manager', 'viewer'],
    children: [
      {
        text: 'Mensualidades',
        icon: <PaymentIcon />,
        path: '/monthly-payments',
        roles: ['admin', 'manager', 'viewer']
      },
      {
        text: 'Pagos Adicionales',
        icon: <ReceiptLongIcon />,
        path: '/additional-payments',
        roles: ['admin', 'manager', 'viewer']
      },
      {
        text: 'Pagos a Profesores',
        icon: <SchoolIcon />,
        path: '/teacher-payments',
        roles: ['admin', 'manager', 'viewer']
      }
    ]
  },
  {
    text: 'Finanzas',
    icon: <AccountBalanceIcon />,
    path: '#',
    roles: ['admin', 'manager', 'viewer'],
    children: [
      {
        text: 'Ofrendas',
        icon: <VolunteerActivismIcon />,
        path: '/offerings',
        roles: ['admin', 'manager', 'viewer']
      },
      {
        text: 'Gastos',
        icon: <ReceiptLongIcon />,
        path: '/expenses',
        roles: ['admin', 'manager', 'viewer']
      }
    ]
  },
  {
  text: 'Reportes',
  icon: <AssessmentIcon />,
  path: '#',
  roles: ['admin', 'manager', 'viewer'],
  children: [
    {
      text: 'Reportes Financieros',
      icon: <AssessmentIcon />,
      path: '/reports/financial',
      roles: ['admin', 'manager', 'viewer']
    },
    {
      text: 'Balance Mensual',
      icon: <AccountBalanceIcon />,
      path: '/reports/balance',
      roles: ['admin', 'manager', 'viewer']
    },
    {
      text: 'Estudiantes Morosos',
      icon: <WarningIcon />,
      path: '/reports/morosos',
      roles: ['admin', 'manager', 'viewer']
    },
    {
      text: 'Ingresos vs Gastos',
      icon: <TrendingUpIcon />,
      path: '/reports/ingresos-gastos',
      roles: ['admin', 'manager', 'viewer']
    }
  ]
},
  {
    text: 'Configuración',
    icon: <SettingsIcon />,
    path: '/settings',
    roles: ['admin']
  }
];

const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  
  const { sidebarOpen } = useSelector((state: RootState) => state.ui);
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(() => {
    return localStorage.getItem('openSubmenu') || null;
  });

  const filteredMenuItems = menuItems.filter(item =>
    user ? item.roles.includes(user.role) : false
  );

  const handleToggleSubmenu = (text: string) => {
    setOpenSubmenu(prev => {
      const newValue = prev === text ? null : text;
      localStorage.setItem('openSubmenu', newValue || '');
      return newValue;
    });
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleTheme = () => {
    dispatch(setThemeMode(theme.palette.mode === 'light' ? 'dark' : 'light'));
  };

  const drawerWidth = 240;

  // Renderizar items de menú - VERSIÓN CORREGIDA
  const renderMenuItem = (item: MenuItemType, level: number = 0) => {
  const hasChildren = item.children && item.children.length > 0;
  const isOpen = openSubmenu === item.text;
  const isSelected = location.pathname === item.path;
  
  const getPaddingLeft = () => {
    if (!sidebarOpen) return 0;
    switch (level) {
      case 0: return 2;      // Nivel principal
      case 1: return 4;      // Primer subnivel
      case 2: return 6;      // Segundo subnivel
      default: return 2 + (level * 2);
    }
  };
  
  const paddingLeft = getPaddingLeft();

  if (hasChildren) {
    return (
      <React.Fragment key={item.text}>
        <ListItem
          component="div"
          onClick={() => sidebarOpen && handleToggleSubmenu(item.text)}
          sx={{
            pl: paddingLeft,
            pr: 2,
            py: 0.75,
            cursor: sidebarOpen ? 'pointer' : 'default',
            '&:hover': {
              backgroundColor: sidebarOpen ? theme.palette.action.hover : 'transparent',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, justifyContent: 'center' }}>
            {item.icon}
          </ListItemIcon>
          {sidebarOpen && (
            <>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ variant: 'body2' }}
              />
              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </>
          )}
        </ListItem>
        <Collapse in={isOpen && sidebarOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {/* 👈 Los hijos comienzan con level 1 */}
            {item.children?.map(child => renderMenuItem(child, 1))}
          </List>
        </Collapse>
      </React.Fragment>
    );
  }

  // Items sin hijos
  return (
    <ListItem
      component="div"
      key={item.text}
      onClick={() => navigate(item.path)}
      sx={{
        pl: paddingLeft,
        pr: 2,
        py: 0.75,
        cursor: 'pointer',
        backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
        color: isSelected ? theme.palette.primary.contrastText : 'inherit',
        '&:hover': {
          backgroundColor: isSelected ? theme.palette.primary.dark : theme.palette.action.hover,
        },
        '& .MuiListItemIcon-root': {
          color: isSelected ? theme.palette.primary.contrastText : 'inherit',
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 40, justifyContent: 'center' }}>
        {item.icon}
      </ListItemIcon>
      {sidebarOpen && (
        <ListItemText 
          primary={item.text} 
          primaryTypographyProps={{ variant: 'body2' }}
        />
      )}
    </ListItem>
  );
};

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(sidebarOpen && {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={() => dispatch(toggleSidebar())}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          {!sidebarOpen && (
            <Box sx={{ flexGrow: 1 }}>
              <Logo size="small" showText={false} />
            </Box>
          )}
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {location.pathname === '/dashboard' ? 'Panel de Control' : 
             location.pathname.startsWith('/students') ? 'Estudiantes' :
             location.pathname.startsWith('/parents') ? 'Padres' :
             location.pathname.startsWith('/teachers') ? 'Profesores' :
             location.pathname.startsWith('/payments') ? 'Pagos' :
             location.pathname.startsWith('/reports') ? 'Reportes' : 'Sistema Financiero Escuela Origen'}
          </Typography>

          <IconButton color="inherit" onClick={handleNotificationOpen}>
            <Badge badgeContent={3} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <IconButton color="inherit" onClick={toggleTheme}>
            {theme.palette.mode === 'dark' ? '🌙' : '☀️'}
          </IconButton>

          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            color="inherit"
            sx={{ ml: 2 }}
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.name?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: sidebarOpen ? drawerWidth : 72,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          '& .MuiDrawer-paper': {
            width: sidebarOpen ? drawerWidth : 72,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
            backgroundColor: theme.palette.background.paper,
            borderRight: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Toolbar>
          <Logo variant="full" size={sidebarOpen ? 'large' : 'small'} />
        </Toolbar>
        <List sx={{ px: 1 }}>
          {filteredMenuItems.map(item => renderMenuItem(item))}
        </List>
        <Divider />
        <List sx={{ px: 1 }}>
          <ListItem
            component="div"
            onClick={handleLogout}
            sx={{
              py: 1,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, justifyContent: 'center' }}>
              <LogoutIcon />
            </ListItemIcon>
            {sidebarOpen && <ListItemText primary="Cerrar Sesión" />}
          </ListItem>
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: `calc(100% - ${sidebarOpen ? drawerWidth : 72}px)`,
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar />
        {children}
      </Box>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem disabled>
          <Typography variant="body2">{user?.name}</Typography>
        </MenuItem>
        <MenuItem disabled>
          <Typography variant="caption">{user?.email}</Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>Cerrar Sesión</MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={() => setNotificationAnchorEl(null)}
      >
        <MenuItem disabled>Sin notificaciones</MenuItem>
      </Menu>
    </Box>
  );
};

export default DashboardLayout;